import React, { useState } from 'react';
import { ArrowUpRight, CalendarClock, CheckCircle2, Map, PlayCircle } from 'lucide-react';
import Alt6Layout from '../components/Alt6Layout';
import { Eyebrow, Reveal } from '../components/Alt6Shared';

const initialForm = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  practiceName: '',
  providers: '',
  role: '',
};

const ROLE_OPTIONS = [
  'Owner / dentist',
  'Executive leader',
  'Operations leader',
  'Practice leader',
  'Technology leader',
  'Other',
];

export default function Alt6Demo() {
  const [formData, setFormData] = useState(initialForm);
  const [submitState, setSubmitState] = useState('idle');

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitState('submitting');
    try {
      const isLocalhost =
        window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
      const apiUrl = process.env.REACT_APP_API_URL || (isLocalhost ? 'http://localhost:8011' : '');
      const response = await fetch(`${apiUrl}/api/demo-request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      if (!response.ok) throw new Error('Request failed');
      setSubmitState('success');
    } catch (error) {
      setSubmitState('error');
    }
  };

  return (
    <Alt6Layout title="Book a demo">
      <section className="a6-page-hero">
        <div className="a6-container a6-page-hero-inner">
          <Reveal>
            <Eyebrow>Book a demo</Eyebrow>
          </Reveal>
          <Reveal delay={0.06}>
            <h1>
              See your practice <em>on autopilot</em>
            </h1>
          </Reveal>
        </div>
      </section>

      <section className="a6-section-tight" style={{ paddingTop: 40 }}>
        <div className="a6-container">
          <div className="a6-form-wrap">
            <Reveal>
              <h2 className="a6-h2" style={{ fontSize: 30 }}>
                What to expect
              </h2>
              <ul className="a6-expect">
                <li>
                  <span className="a6-expect-icon">
                    <CalendarClock size={18} strokeWidth={2.2} />
                  </span>
                  <span>
                    <strong>A 30-minute walkthrough</strong>
                    <p>
                      With someone who builds the product — no scripted sales deck, no
                      pressure.
                    </p>
                  </span>
                </li>
                <li>
                  <span className="a6-expect-icon">
                    <PlayCircle size={18} strokeWidth={2.2} />
                  </span>
                  <span>
                    <strong>Autopilot, live</strong>
                    <p>
                      Watch the AI answer a call, verify benefits, file a claim, and
                      fill a schedule gap in real time.
                    </p>
                  </span>
                </li>
                <li>
                  <span className="a6-expect-icon">
                    <Map size={18} strokeWidth={2.2} />
                  </span>
                  <span>
                    <strong>A plan for your practice</strong>
                    <p>
                      What migrating looks like from your current system, and how
                      autopilot ramps up from there.
                    </p>
                  </span>
                </li>
              </ul>
            </Reveal>

            <Reveal delay={0.1}>
              <div className="a6-form-card">
                {submitState === 'success' ? (
                  <div className="a6-form-success" role="status">
                    <CheckCircle2 size={44} strokeWidth={1.8} color="var(--a6-green)" />
                    <h3>Your request is in.</h3>
                    <p>
                      Our team will reach out shortly to find a time that works. Talk
                      soon.
                    </p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit}>
                    <div className="a6-form-grid">
                      <div className="a6-field">
                        <label htmlFor="a6-firstName">First name</label>
                        <input
                          id="a6-firstName"
                          name="firstName"
                          autoComplete="given-name"
                          required
                          value={formData.firstName}
                          onChange={handleChange}
                        />
                      </div>
                      <div className="a6-field">
                        <label htmlFor="a6-lastName">Last name</label>
                        <input
                          id="a6-lastName"
                          name="lastName"
                          autoComplete="family-name"
                          required
                          value={formData.lastName}
                          onChange={handleChange}
                        />
                      </div>
                      <div className="a6-field">
                        <label htmlFor="a6-email">Work email</label>
                        <input
                          id="a6-email"
                          name="email"
                          type="email"
                          autoComplete="email"
                          required
                          value={formData.email}
                          onChange={handleChange}
                        />
                      </div>
                      <div className="a6-field">
                        <label htmlFor="a6-phone">Phone</label>
                        <input
                          id="a6-phone"
                          name="phone"
                          type="tel"
                          autoComplete="tel"
                          required
                          value={formData.phone}
                          onChange={handleChange}
                        />
                      </div>
                      <div className="a6-field is-full">
                        <label htmlFor="a6-practiceName">Practice name</label>
                        <input
                          id="a6-practiceName"
                          name="practiceName"
                          autoComplete="organization"
                          required
                          value={formData.practiceName}
                          onChange={handleChange}
                        />
                      </div>
                      <div className="a6-field">
                        <label htmlFor="a6-providers">Number of providers</label>
                        <input
                          id="a6-providers"
                          name="providers"
                          inputMode="numeric"
                          value={formData.providers}
                          onChange={handleChange}
                        />
                      </div>
                      <div className="a6-field">
                        <label htmlFor="a6-role">Your role</label>
                        <select
                          id="a6-role"
                          name="role"
                          required
                          value={formData.role}
                          onChange={handleChange}
                        >
                          <option value="" disabled>
                            Select one
                          </option>
                          {ROLE_OPTIONS.map((role) => (
                            <option key={role} value={role}>
                              {role}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <button
                      type="submit"
                      className="a6-btn a6-btn-primary"
                      disabled={submitState === 'submitting'}
                    >
                      {submitState === 'submitting' ? 'Sending…' : 'Request a demo'}
                      <ArrowUpRight size={17} strokeWidth={2.4} aria-hidden="true" />
                    </button>
                    {submitState === 'error' ? (
                      <p className="a6-form-error" role="alert">
                        Something went wrong sending your request. Please try again.
                      </p>
                    ) : null}
                    <p className="a6-form-note">
                      No spam, no obligation. We'll only use this to set up your
                      walkthrough.
                    </p>
                  </form>
                )}
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </Alt6Layout>
  );
}
